import React, { useEffect } from 'react';
import { Modal } from '@/components/modal/Modal';
import { TextInput } from '@/components/form/fields/text/TextInput';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AddressRequest } from '@/services/api/contracts/address';

export interface SaveAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData: Omit<AddressRequest, 'name'> | null;
  onSave: (finalAddress: AddressRequest) => Promise<void>;
}

export const SaveAddressModal: React.FC<SaveAddressModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onSave
}) => {
  const { control, handleSubmit, reset, formState: { isSubmitting, errors } } = useForm<AddressRequest>({
    resolver: zodResolver(AddressRequest),
    defaultValues: {
      name: '',
      line1: '',
      city: '',
      postcode: ''
    }
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        name: '',
        line1: initialData?.line1 || '',
        line2: initialData?.line2 || undefined,
        city: initialData?.city || '',
        state: initialData?.state || undefined,
        postcode: initialData?.postcode || '',
        country: initialData?.country || 'UK'
      });
    }
  }, [isOpen, initialData, reset]);

  const onSubmit = async (data: AddressRequest) => {
    try {
      await onSave(data);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal isOpen={isOpen} open={() => {}} close={onClose} className="max-w-[500px] bg-white p-6 rounded-lg overflow-visible">
      <Modal.Controls close={onClose} isOpen={isOpen} open={() => {}}>
        <h2 className="text-lg font-semibold">Save Address to Address Book</h2>
      </Modal.Controls>
      <form 
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }} 
        className="flex flex-col gap-4 py-4 text-slate-800 dark:text-slate-100"
      >
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Location Name / Alias</label>
              <TextInput 
                id="save-name" 
                value={field.value} 
                onChange={field.onChange} 
                placeholder="e.g. Headquarters, Warehouse"
              />
              {errors.name && <span className="text-red-500 text-xs mt-1">{errors.name.message}</span>}
            </div>
          )}
        />
        
        <Controller
          name="line1"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Line 1</label>
              <TextInput id="save-line1" value={field.value} onChange={field.onChange} />
              {errors.line1 && <span className="text-red-500 text-xs mt-1">{errors.line1.message}</span>}
            </div>
          )}
        />

        <Controller
          name="line2"
          control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium mb-1">Line 2</label>
              <TextInput id="save-line2" value={field.value || ''} onChange={field.onChange} />
            </div>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <TextInput id="save-city" value={field.value} onChange={field.onChange} />
                {errors.city && <span className="text-red-500 text-xs mt-1">{errors.city.message}</span>}
              </div>
            )}
          />

          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">State / Province</label>
                <TextInput id="save-state" value={field.value || ''} onChange={field.onChange} />
              </div>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="postcode"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Postcode</label>
                <TextInput id="save-postcode" value={field.value} onChange={field.onChange} />
                {errors.postcode && <span className="text-red-500 text-xs mt-1">{errors.postcode.message}</span>}
              </div>
            )}
          />

          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <TextInput id="save-country" value={field.value || ''} onChange={field.onChange} />
              </div>
            )}
          />
        </div>

        <div className="flex justify-end space-x-2 mt-4">
          <Button variant={ButtonVariant.Secondary} onClick={onClose} disabled={isSubmitting} type="button">Cancel</Button>
          <Button variant={ButtonVariant.Primary} type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
